import { Injectable, computed, signal } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { TabRouteCacheRegistry } from '@/app/core/routing/tab-route-cache.registry';
import { normalizeTabRouteKey } from '@/app/core/routing/tab-route-key';

export interface TabItem {
    route: string;
    title: string;
    tabType?: string;
    mode?: string;
    /** Set for `tabType === 'form'`: show ` (…)` after translated title. */
    formTabSuffix?: 'add' | 'docId';
    formTabDocId?: string;
}

@Injectable({ providedIn: 'root' })
export class TabService {
    private readonly _tabs = signal<TabItem[]>([]);
    private readonly _currentRoute = signal<string>('');

    private readonly _tabRemovedSource = new Subject<string>();
    readonly tabRemoved$: Observable<string> = this._tabRemovedSource.asObservable();

    private readonly _gridRefreshSource = new Subject<string>();
    readonly gridRefresh$: Observable<string> = this._gridRefreshSource.asObservable();

    readonly tabs = this._tabs.asReadonly();
    readonly currentRoute = this._currentRoute.asReadonly();

    readonly currentTab = computed(() => this._tabs().find((t) => t.route === this._currentRoute()) ?? null);

    constructor(private readonly routeCache: TabRouteCacheRegistry) {}

    /**
     * Opens or focuses a tab. Called on every NavigationEnd.
     */
    openTab(route: string, data?: Record<string, any>): void {
        this._currentRoute.set(route);

        const existing = this._tabs().find((t) => t.route === route);
        if (!existing) {
            const qp = (data?.['safeQueryParams'] ?? {}) as Record<string, string | undefined>;
            const modeRaw = qp['mode'];
            const mode = modeRaw != null && modeRaw !== '' ? String(modeRaw).toLowerCase() : undefined;
            const id = String(qp['id'] ?? '').trim();
            const formSuffix = this.computeFormTabSuffix(data?.['tabType'], mode, id);

            const titleKey = data?.['tabTitle'] ?? data?.['title'];
            const tab: TabItem = {
                route,
                title: titleKey ?? this.inferTitle(route),
                tabType: data?.['tabType'],
                mode: modeRaw,
                formTabSuffix: formSuffix.kind,
                formTabDocId: formSuffix.docId,
            };
            this._tabs.update((tabs) => [...tabs, tab]);
        }
    }

    private computeFormTabSuffix(
        tabType: string | undefined,
        mode: string | undefined,
        id: string,
    ): { kind?: 'add' | 'docId'; docId?: string } {
        if (tabType !== 'form') {
            return {};
        }
        if (mode === 'add') {
            return { kind: 'add' };
        }
        if (mode === 'edit' || mode === 'view') {
            return { kind: 'docId', docId: id.length > 0 ? id : '—' };
        }
        if (id.length > 0) {
            return { kind: 'docId', docId: id };
        }
        return { kind: 'add' };
    }

    /**
     * Removes a tab by route. Returns the route to navigate to, or null.
     */
    closeTab(route: string): string | null {
        const tabs = this._tabs();
        const idx = tabs.findIndex((t) => t.route === route);
        if (idx === -1) return null;

        if (tabs.length <= 1) return null;

        const isCurrent = this._currentRoute() === route;
        this._tabs.update((ts) => ts.filter((t) => t.route !== route));
        this.routeCache.delete(normalizeTabRouteKey(route));
        this._tabRemovedSource.next(route);

        if (isCurrent) {
            const remaining = this._tabs();
            const next = remaining[Math.min(idx, remaining.length - 1)];
            return next?.route ?? null;
        }
        return null;
    }

    closeTabsToRight(route: string): void {
        const tabs = this._tabs();
        const idx = tabs.findIndex((t) => t.route === route);
        if (idx === -1) return;
        const removed = tabs.slice(idx + 1);
        this._tabs.update((ts) => ts.slice(0, idx + 1));
        removed.forEach((t) => {
            this.routeCache.delete(normalizeTabRouteKey(t.route));
            this._tabRemovedSource.next(t.route);
        });
    }

    closeTabsToLeft(route: string): void {
        const tabs = this._tabs();
        const idx = tabs.findIndex((t) => t.route === route);
        if (idx === -1) return;
        const removed = tabs.slice(0, idx);
        this._tabs.update((ts) => ts.slice(idx));
        removed.forEach((t) => {
            this.routeCache.delete(normalizeTabRouteKey(t.route));
            this._tabRemovedSource.next(t.route);
        });
    }

    closeOtherTabs(route: string): void {
        const tabs = this._tabs();
        const kept = tabs.find((t) => t.route === route);
        if (!kept) return;
        const removed = tabs.filter((t) => t.route !== route);
        this._tabs.set([kept]);
        removed.forEach((t) => {
            this.routeCache.delete(normalizeTabRouteKey(t.route));
            this._tabRemovedSource.next(t.route);
        });
    }

    refreshGrid(route: string): void {
        this._gridRefreshSource.next(route);
    }

    reset(): void {
        this._tabs.set([]);
        this._currentRoute.set('');
        this.routeCache.clearAll();
    }

    private inferTitle(route: string): string {
        const segments = route.split('/').filter(Boolean);
        const last = segments[segments.length - 1] ?? 'dashboard';
        return last
            .replace(/([A-Z])/g, ' $1')
            .replace(/[-_]/g, ' ')
            .trim();
    }
}
